import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtNOK, fmtDate, fmtKwh } from "@/lib/format";
import { Download, ArrowLeft, FileText } from "lucide-react";

export function PortalFakturaer() {
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/portal/invoices").then(({ data }) => setItems(data)); }, []);

  return (
    <div data-testid="portal-fakturaer-page">
      <PageHeader title="Mine fakturaer" />
      <Card className="overflow-hidden">
        {!items ? <div className="p-8"><Spinner /></div>
          : items.length === 0 ? <Empty title="Ingen fakturaer" />
          : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">Fakturanr</th>
                <th className="py-2 px-4 font-medium">Dato</th>
                <th className="py-2 px-4 font-medium">Forfall</th>
                <th className="py-2 px-4 font-medium">Forbruk</th>
                <th className="py-2 px-4 font-medium text-right">Total</th>
                <th className="py-2 px-4 font-medium">Status</th>
                <th className="py-2 px-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`portal-inv-${i.invoice_number}`}>
                  <td className="py-2 px-4"><Link to={`/portal/fakturaer/${i.id}`} className="font-mono text-xs text-emerald-400">{i.invoice_number}</Link></td>
                  <td className="py-2 px-4 text-zinc-400">{fmtDate(i.issue_date)}</td>
                  <td className="py-2 px-4 text-zinc-400">{fmtDate(i.due_date)}</td>
                  <td className="py-2 px-4 tabular-nums text-zinc-300">{fmtKwh(i.consumption_kwh)}</td>
                  <td className="py-2 px-4 tabular-nums text-zinc-100 text-right">{fmtNOK(i.total_nok)}</td>
                  <td className="py-2 px-4"><StatusBadge value={i.status} /></td>
                  <td className="py-2 px-4">
                    <a href={`${API}/portal/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer"
                      onClick={async (e) => { e.preventDefault(); const r = await fetch(`${API}/portal/invoices/${i.id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } }); const b = await r.blob(); window.open(URL.createObjectURL(b)); }}
                      className="text-emerald-400 text-xs inline-flex items-center gap-1 hover:underline">
                      <Download className="h-3 w-3" /> PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export function PortalFaktura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  useEffect(() => { api.get(`/portal/invoices/${id}`).then(({ data }) => setInv(data)).catch(() => setInv({ error: true })); }, [id]);

  const dl = async () => {
    const r = await fetch(`${API}/portal/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const b = await r.blob(); window.open(URL.createObjectURL(b));
  };

  if (!inv) return <Spinner />;
  if (inv.error) return <Empty title="Faktura ikke funnet" />;

  return (
    <div data-testid="portal-faktura-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> Tilbake
      </button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Faktura {inv.invoice_number}</h1>
            <StatusBadge value={inv.status} />
          </div>
          <div className="text-sm text-zinc-500 mt-1">KID {inv.kid} · utstedt {fmtDate(inv.issue_date)} · forfall {fmtDate(inv.due_date)}</div>
        </div>
        <button onClick={dl} className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm flex items-center gap-2" data-testid="portal-pdf-btn">
          <Download className="h-4 w-4" /> Last ned PDF
        </button>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-zinc-900 font-heading font-medium">Fakturalinjer</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
              <th className="py-2 px-4 font-medium">Beskrivelse</th>
              <th className="py-2 px-4 font-medium text-right">Antall</th>
              <th className="py-2 px-4 font-medium">Enhet</th>
              <th className="py-2 px-4 font-medium text-right">Pris</th>
              <th className="py-2 px-4 font-medium text-right">Sum</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines?.map((l, i) => (
              <tr key={i} className="border-t border-zinc-900">
                <td className="py-2 px-4 text-zinc-100">{l.description}</td>
                <td className="py-2 px-4 tabular-nums text-right text-zinc-300">{l.quantity.toFixed(2)}</td>
                <td className="py-2 px-4 text-zinc-400 text-xs">{l.unit}</td>
                <td className="py-2 px-4 tabular-nums text-right text-zinc-300">{l.unit_price_nok.toFixed(4)}</td>
                <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(l.amount_nok, { decimals: 2 })}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-zinc-800">
            <tr>
              <td colSpan="4" className="py-2 px-4 text-right text-zinc-400 text-xs">Sum eks. mva</td>
              <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(inv.subtotal_nok + inv.electricity_tax_nok, { decimals: 2 })}</td>
            </tr>
            <tr>
              <td colSpan="4" className="py-2 px-4 text-right text-zinc-400 text-xs">MVA (25 %)</td>
              <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(inv.vat_nok, { decimals: 2 })}</td>
            </tr>
            {inv.stromstotte_nok !== 0 && (
              <tr>
                <td colSpan="4" className="py-2 px-4 text-right text-emerald-400 text-xs">Strømstøtte</td>
                <td className="py-2 px-4 tabular-nums text-right text-emerald-400">{fmtNOK(inv.stromstotte_nok, { decimals: 2 })}</td>
              </tr>
            )}
            <tr className="bg-zinc-950/40">
              <td colSpan="4" className="py-3 px-4 text-right font-heading font-medium text-zinc-100">Totalt å betale</td>
              <td className="py-3 px-4 tabular-nums text-right font-heading font-medium text-emerald-400 text-lg">{fmtNOK(inv.total_nok, { decimals: 2 })}</td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}
